import { useState, useEffect } from 'react';
import type { EWayBillRecord } from '@/api/compliance.api';

interface PrintEWayBillProps {
  ewb: EWayBillRecord;
  onClose: () => void;
}

function formatINR(n?: number | null): string {
  if (n == null || isNaN(Number(n))) return '₹0.00';
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
  } catch {
    return iso;
  }
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const dateStr = d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    const timeStr = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase();
    return `${dateStr} ${timeStr}`;
  } catch {
    return iso;
  }
}

function getStateName(gstinOrCode?: string): string {
  if (!gstinOrCode) return '—';
  const code = gstinOrCode.length >= 2 ? gstinOrCode.substring(0, 2) : gstinOrCode;
  const stateMap: Record<string, string> = {
    '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh',
    '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan',
    '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh',
    '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram', '16': 'Tripura',
    '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal', '20': 'Jharkhand',
    '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
    '25': 'Daman & Diu', '26': 'Dadra & Nagar Haveli', '27': 'Maharashtra',
    '29': 'Karnataka', '30': 'Goa', '31': 'Lakshadweep', '32': 'Kerala',
    '33': 'Tamil Nadu', '34': 'Puducherry', '35': 'Andaman & Nicobar',
    '36': 'Telangana', '37': 'Andhra Pradesh',
  };
  const name = stateMap[code] || `State Code ${code}`;
  return `${name} (${code})`;
}

function amountToWords(n: number): string {
  const num = Math.round(n);
  if (!num) return 'Zero Rupees Only';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function inWords(n: number): string {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
    if (n < 100000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
    return inWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
  }
  return `INR ${inWords(num)} Only`;
}



export default function PrintEWayBillView({ ewb, onClose }: PrintEWayBillProps) {
  const [company, setCompany] = useState<{
    name?: string;
    address?: string;
    gstin?: string;
    phone?: string;
    email?: string;
    pan?: string;
    website?: string;
  } | null>(null);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/company/get`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const result = await response.json();
        if (result?.success && result?.data) {
          setCompany(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch company details:', error);
      }
    };
    fetchCompany();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  // Dynamic Supplier (From / Consignor)
  const supplierName    = ewb.supplierName  || company?.name    || 'Company Name';
  const supplierGSTIN   = ewb.fromGSTIN     || company?.gstin   || '—';
  const supplierPAN     = supplierGSTIN.length >= 12 ? supplierGSTIN.substring(2, 12) : (company?.pan || '—');
  const supplierAddress = ewb.fromAddress   || company?.address || '—';
  const supplierPhone   = company?.phone    || '—';
  const supplierEmail   = company?.email    || '—';
  const supplierWebsite = company?.website  || '';

  // Dynamic Customer (To / Consignee)
  const recipientName    = ewb.customerName || 'Customer';
  const recipientGSTIN   = ewb.toGSTIN      || 'URP';
  const recipientAddress = ewb.toAddress    || '—';

  const stateCodeFrom = supplierGSTIN && supplierGSTIN.length >= 2 ? supplierGSTIN.substring(0, 2) : '—';
  const stateCodeTo   = recipientGSTIN && recipientGSTIN.length >= 2 && recipientGSTIN !== 'URP'
    ? recipientGSTIN.substring(0, 2)
    : (stateCodeFrom || '—');

  // Dynamic Items list
  const itemList = Array.isArray(ewb.items) && ewb.items.length > 0
    ? ewb.items
    : [
        {
          productName: 'Supply of Goods / Services',
          itemCode: '',
          hsnCode: '',
          quantity: 1,
          qtyUnit: 'NOS',
          rate: Number(ewb.totalTaxableValue) || Number(ewb.totalValue) || 0,
          taxableAmount: Number(ewb.totalTaxableValue) || 0,
          totalAmount: Number(ewb.totalValue) || 0,
        },
      ];

  const totalQty = itemList.reduce((sum, item: any) => sum + (Number(item.quantity || item.qty) || 1), 0);
  const totalTaxable = Number(ewb.totalTaxableValue) || itemList.reduce((sum, item: any) => sum + (Number(item.taxableAmount || item.taxableValue) || 0), 0);
  const grandTotal   = Number(ewb.totalValue) || (totalTaxable + Number(ewb.cgst || 0) + Number(ewb.sgst || 0) + Number(ewb.igst || 0));

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[60] flex items-start justify-center overflow-y-auto py-6 print:p-0 print:bg-white print:static"
      onClick={onClose}
    >
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          .print-area, .print-area * {
            visibility: visible !important;
          }
          .print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 10mm !important;
            background: white !important;
            box-shadow: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          @page {
            size: A4 portrait;
            margin: 6mm;
          }
        }
      `}</style>

      {/* Outer Card Container */}
      <div
        className="bg-white w-[1000px] max-w-full shadow-2xl rounded-2xl overflow-hidden print:shadow-none print:w-full print:rounded-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Control Bar (Hidden in Print) */}
        <div className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2">
            <i className="ri-printer-line text-indigo-400 text-lg" />
            <span className="text-sm font-semibold">Print E-Way Bill Slip — {ewb.ewbNumber}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-printer-line" /> Print Document
            </button>
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        {/* Printable E-Way Bill Document Page */}
        <div className="print-area p-8 print:p-0 text-slate-800 font-sans text-xs bg-white flex flex-col space-y-4">
          
          {/* Header Bar */}
          <div className="flex justify-between items-start border-b border-slate-200 pb-3">
            <div>
              <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">{supplierName}</h1>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                Government of India • GST E-Way Bill System • Form GST {ewb.documentType || 'EWB-01'}
              </p>
            </div>
            <div className="text-right">
              <span className="inline-block px-3 py-1 bg-indigo-50 border border-indigo-200 text-indigo-900 text-2xl font-extrabold rounded-lg tracking-wider uppercase">
                E-Way Bill
              </span>
              <p className="text-[11px] text-slate-500 mt-1 font-mono">Original for Consignee / Carrier</p>
            </div>
          </div>

          {/* Top Info Grid: EWB Details */}
          <div className="border border-slate-200 rounded-xl p-3.5 bg-slate-50/60 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-x-4 gap-y-2 text-xs">
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500">E-Way Bill No.</span>
              <span className="font-mono font-extrabold text-indigo-700 text-sm">{ewb.ewbNumber}</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500">Generated Date & Time</span>
              <span className="font-semibold text-slate-800">{formatDateTime(ewb.generatedDate)}</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500">Valid Until</span>
              <span className="font-bold text-emerald-700">{formatDateTime(ewb.validUntil)}</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500">Status</span>
              <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                {ewb.status || 'ACTIVE'}
              </span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500">Document No. / Type</span>
              <span className="font-semibold text-slate-800">{ewb.invoiceNumber || '—'} (Tax Invoice)</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500">Document Date</span>
              <span className="font-semibold text-slate-800">{formatDate(ewb.generatedDate)}</span>
            </div>
          </div>

          {/* Parties: Consignor (From) & Consignee (To) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            
            {/* Consignor (From / Supplier) */}
            <div className="border border-slate-200 rounded-xl p-3.5 bg-white space-y-1.5">
              <div className="flex items-center justify-between border-b border-slate-100 pb-1">
                <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">PART-A: FROM (CONSIGNOR)</span>
                <span className="text-[10px] font-semibold text-slate-500">State: {getStateName(stateCodeFrom)}</span>
              </div>
              <p className="font-bold text-slate-900 text-sm">{supplierName}</p>
              <div className="grid grid-cols-[60px_1fr] gap-1 text-xs">
                <span className="text-slate-500">GSTIN:</span>
                <span className="font-mono font-bold text-slate-900">{supplierGSTIN}</span>
                <span className="text-slate-500">PAN:</span>
                <span className="font-mono font-medium text-slate-800">{supplierPAN}</span>
                <span className="text-slate-500">Address:</span>
                <span className="text-slate-700 leading-snug">{supplierAddress}</span>
                {supplierPhone !== '—' && (
                  <>
                    <span className="text-slate-500">Phone:</span>
                    <span className="text-slate-700">{supplierPhone}</span>
                  </>
                )}
                {supplierEmail !== '—' && (
                  <>
                    <span className="text-slate-500">Email:</span>
                    <span className="text-slate-700">{supplierEmail}</span>
                  </>
                )}
              </div>
            </div>

            {/* Consignee (To / Customer) */}
            <div className="border border-slate-200 rounded-xl p-3.5 bg-white space-y-1.5">
              <div className="flex items-center justify-between border-b border-slate-100 pb-1">
                <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">PART-A: TO (CONSIGNEE)</span>
                <span className="text-[10px] font-semibold text-slate-500">Place of Supply: {getStateName(stateCodeTo)}</span>
              </div>
              <p className="font-bold text-slate-900 text-sm">{recipientName}</p>
              <div className="grid grid-cols-[60px_1fr] gap-1 text-xs">
                <span className="text-slate-500">GSTIN:</span>
                <span className="font-mono font-bold text-slate-900">{recipientGSTIN}</span>
                <span className="text-slate-500">Address:</span>
                <span className="text-slate-700 leading-snug">{recipientAddress}</span>
              </div>
            </div>

          </div>

          {/* Part-B: Transportation Details */}
          <div className="border border-slate-200 rounded-xl p-3.5 bg-slate-50/60">
            <div className="border-b border-slate-200 pb-1 mb-2 flex items-center justify-between">
              <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">PART-B: TRANSPORT & VEHICLE DETAILS</span>
              <span className="text-[10px] font-semibold text-slate-500">Road Transport Rule 138(2)</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="block text-[10px] text-slate-500 uppercase font-semibold">Mode</span>
                <span className="font-bold text-slate-900">{ewb.transportMode || ewb.vehicleType || 'Road'}</span>
              </div>
              <div>
                <span className="block text-[10px] text-slate-500 uppercase font-semibold">Vehicle Number</span>
                <span className="font-mono font-bold text-indigo-700">{ewb.vehicleNumber || 'Not Assigned'}</span>
              </div>
              <div>
                <span className="block text-[10px] text-slate-500 uppercase font-semibold">Transporter ID / Name</span>
                <span className="font-semibold text-slate-900">
                  {ewb.transporterName || ewb.transporterId || 'Self Managed'}
                </span>
              </div>
              <div>
                <span className="block text-[10px] text-slate-500 uppercase font-semibold">Approx. Distance</span>
                <span className="font-bold text-slate-900">
                  {ewb.distanceKm && Number(ewb.distanceKm) > 0 ? `${ewb.distanceKm} km` : '100 km'}
                </span>
              </div>
            </div>
          </div>

          {/* Item Breakdown Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <table className="w-full border-collapse text-[11px]">
              <thead>
                <tr className="bg-slate-100/80 text-slate-700 font-bold border-b border-slate-200 text-center">
                  <th className="p-2 w-10 border-r border-slate-200">#</th>
                  <th className="p-2 text-left border-r border-slate-200">Item Description</th>
                  <th className="p-2 w-20 border-r border-slate-200">Item Code</th>
                  <th className="p-2 w-20 border-r border-slate-200">HSN</th>
                  <th className="p-2 w-16 border-r border-slate-200">Qty</th>
                  <th className="p-2 w-16 border-r border-slate-200">Unit</th>
                  <th className="p-2 w-24 text-right border-r border-slate-200">Rate (₹)</th>
                  <th className="p-2 w-28 text-right">Total Amount (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {itemList.map((item: any, idx: number) => {
                  const lineTaxable = Number(item.taxableAmount || item.taxableValue) || 0;
                  const lineCGST    = Number(item.cgstAmt || item.cgst) || 0;
                  const lineSGST    = Number(item.sgstAmt || item.sgst) || 0;
                  const lineIGST    = Number(item.igstAmt || item.igst) || 0;
                  const lineQty     = Number(item.quantity || item.qty || 1);
                  const lineRate    = Number(item.rate) || (lineQty > 0 && lineTaxable > 0 ? lineTaxable / lineQty : 0);
                  const lineTotal   = Number(item.totalAmount) || (lineTaxable + lineCGST + lineSGST + lineIGST) || (lineRate * lineQty);

                  return (
                    <tr key={idx} className="hover:bg-slate-50/50">
                      <td className="p-2 text-center border-r border-slate-100 text-slate-500">{idx + 1}</td>
                      <td className="p-2 text-left font-semibold text-slate-900 border-r border-slate-100">
                        {item.productName || item.itemName || item.name || 'Goods Supply'}
                      </td>
                      <td className="p-2 text-center border-r border-slate-100 font-mono text-slate-600">
                        {item.itemCode || item.item_code || '—'}
                      </td>
                      <td className="p-2 text-center border-r border-slate-100 font-mono text-slate-700 font-medium">
                        {item.hsnCode || item.hsn || '—'}
                      </td>
                      <td className="p-2 text-center font-bold border-r border-slate-100 text-slate-900">
                        {lineQty}
                      </td>
                      <td className="p-2 text-center border-r border-slate-100 text-slate-600">
                        {item.qtyUnit || item.unit || 'NOS'}
                      </td>
                      <td className="p-2 text-right border-r border-slate-100 whitespace-nowrap font-mono">
                        {lineRate > 0 ? lineRate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                      </td>
                      <td className="p-2 text-right font-bold text-slate-900 whitespace-nowrap font-mono">
                        {formatINR(lineTotal)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-bold text-slate-900 border-t border-slate-200">
                  <td colSpan={4} className="p-2 text-right border-r border-slate-200 uppercase text-[10px] tracking-wider text-slate-500">
                    Total Quantity & Value:
                  </td>
                  <td className="p-2 text-center border-r border-slate-200 font-bold">{totalQty}</td>
                  <td className="p-2 border-r border-slate-200" />
                  <td className="p-2 border-r border-slate-200" />
                  <td className="p-2 text-right font-extrabold text-indigo-700 font-mono text-xs whitespace-nowrap">
                    {formatINR(grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Grand Total & Amount In Words Banner */}
          <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs">
              <span className="font-bold text-slate-500 uppercase text-[10px] tracking-wider block">
                Total Amount in Words:
              </span>
              <span className="font-bold text-slate-900 text-xs">
                {amountToWords(grandTotal)}
              </span>
            </div>
            <div className="text-right shrink-0">
              <span className="text-[10px] text-slate-500 font-semibold uppercase block">
                Total Consignment Value:
              </span>
              <span className="text-lg font-black text-indigo-700 font-mono">
                {formatINR(grandTotal)}
              </span>
            </div>
          </div>

          {/* Footer Signatures & Declaration */}
          <div className="pt-3 border-t border-slate-200 flex items-end justify-between text-xs text-slate-600">
            <div className="max-w-md space-y-1">
              <p className="font-bold text-slate-900">Statutory Declaration:</p>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                This E-Way Bill slip is generated in accordance with Rule 138 of the Central Goods and Services Tax Rules, 2017. 
                The goods described herein are being transported under the authority of this document.
              </p>
              <p className="text-[10px] text-slate-400 font-mono">
                Generated By: {ewb.generatedBy || 'System Admin'} • Document ID: {ewb.id || ewb.ewbNumber}
              </p>
            </div>
            <div className="text-center shrink-0 min-w-[180px]">
              <div className="h-10 border-b border-slate-400 mb-1" />
              <p className="font-bold text-slate-900 text-[11px]">{supplierName}</p>
              <p className="text-[10px] text-slate-500">Authorized Signatory / Consignor</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
